import React, { useEffect } from 'react';
import MaintenanceMiddleware from './middleware/MaintenanceMiddleware';
import { Route, Routes, Outlet, Navigate } from 'react-router-dom';
import { useTabInitialization } from './hooks/useTabInitialization';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/select.css';
import './styles/common.css';
import './styles/variables.css';
import './styles/layout.css';
import DashboardNavbar from './components/dashboard/Navbar';
import Sidebar from './components/dashboard/Sidebar';
import DashboardContent from './components/dashboard/DashboardContent';
import HomePage from './components/home/HomePage';
import HomeLayout from './components/home/HomeLayout';
import Login from './components/auth/Login';
import ResetPassword from './components/auth/ResetPassword';
import AdminLogin from './components/auth/AdminLogin';
import SecureAdminLogin from './components/auth/SecureAdminLogin';
import AdminsPage from './components/dashboard/users/AdminsPage';
import InstructorPage from './components/dashboard/users/InstructorPage';
import TraineesPage from './components/dashboard/users/TraineesPage';
import CourseLearn from './pages/course-learn/CourseLearn';
import CreateCoursePage from './components/dashboard/courses/CreateCoursePage';
import EditCoursePage from './components/dashboard/courses/EditCoursePage';
import CoursesListPage from './components/dashboard/courses/CoursesListPage';
import ExamsPage from './components/dashboard/courses/exams';
import CreateExamPage from './components/dashboard/courses/exams/create';
import EditExamPage from './components/dashboard/courses/exams/edit';
import ExamPreviewPage from './components/dashboard/courses/exams/preview';
import GroupsPage from './components/dashboard/courses/groups';
import ContentPage from './components/dashboard/courses/content';
import CourseContentManager from './components/dashboard/courses/content/manage';
import UserInfoPage from './components/dashboard/devices/users';
import UserDevices from './components/dashboard/devices/UserDevices';
import Settings from './components/dashboard/settings/Settings';
import CourseDiscounts from './components/dashboard/discounts/CourseDiscounts';
import CouponDiscounts from './components/dashboard/discounts/CouponDiscounts';
import PrivateRoute from './components/auth/PrivateRoute';
import { SidebarProvider, useSidebar } from './context/SidebarContext';
import MainLayout from './components/layouts/MainLayout';
import BooksList from './components/dashboard/books/BooksList';
import CreateBook from './components/dashboard/books/CreateBook';
import EditBook from './components/dashboard/books/EditBook';
import BookOrders from './components/dashboard/books/BookOrders';
import CustomerReviews from './components/dashboard/reviews/CustomerReviews';
import ExperiencesPage from './components/dashboard/experiences';
import AskInstructor from './components/dashboard/ask-instructor/AskInstructor';
import ChatCard from './components/dashboard/ask-instructor/ChatCard';
import BankTransfers from './components/dashboard/payments/BankTransfers';
import ElectronicPayment from './components/dashboard/payments/ElectronicPayment';
import CourseChat from './components/dashboard/courses/CourseChat';
import CategoriesPage from './components/dashboard/courses/categories';
import EnrollmentsPage from './components/dashboard/courses/enrollments';
import { api } from './services/api';
import EmptyCart from './components/store/EmptyCart';
import Store from './components/store/Store';
import BookDetails from './components/store/BookDetails';
import Cart from './components/cart/Cart';
import Checkout from './components/checkout/Checkout';
import PaymentSuccess from './pages/payment/Success';
import PaymentError from './pages/payment/Error';
import PaymentCancel from './pages/payment/Cancel';
import OrderConfirmation from './components/checkout/OrderConfirmation';
import { ThemeProvider } from './context/ThemeContext';
import { SettingsProvider } from './context/SettingsContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { setupPermissionsSyncService } from './services/permissionsSyncService';
import About from './pages/About/About';
import Terms from './pages/Terms/Terms';
import FAQ from './pages/FAQ/FAQ';
import Footer from './components/Footer/Footer';
import Register from './pages/Auth/Register';
import CourseDetails from './components/course/CourseDetails';
import Profile from './pages/Profile';
import MyCourses from './pages/MyCourses';
import Payments from './pages/Payments';
import NotFound from './pages/NotFound';
import ChatBot from './components/chatbot/ChatBot';
import UserAskInstructor from './pages/UserAskInstructor';
import InstallmentDetails from './pages/InstallmentDetails';
import CoursesPage from './pages/CoursesPage';

function LearnLayout({ children }) {
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {children}
    </div>
  );
}

function App() {
  // استخدام خطاف التهيئة للتبويب الجديد
  useTabInitialization();
  // مزامنة صلاحيات المستخدم عند بدء التطبيق
  React.useEffect(() => {
    // بعد التحويل إلى sessionStorage، نحتاج لتفعيل setupPermissionsSyncService مرة أخرى
    // لضمان تهيئة الصلاحيات بشكل صحيح عند بدء التطبيق
    setupPermissionsSyncService();
  }, []);

  return (
    <ThemeProvider>
      <SettingsProvider>
        <PermissionsProvider>
          <MaintenanceMiddleware>
            <SidebarProvider>
              <Routes>
                <Route path="/" element={<HomeLayout />}>
                  <Route index element={<HomePage />} />
                  <Route path="about" element={<About />} />
                  <Route path="terms" element={<Terms />} />
                  <Route path="faq" element={<FAQ />} />
                  <Route path="store" element={<Store />} />
                  <Route path="store/books/:bookId" element={<BookDetails />} />
                  <Route path="courses" element={<CoursesPage />} />
                  <Route path="courses/:id" element={<CourseDetails />} />
                  <Route path="payment/success" element={<PaymentSuccess />} />
                  <Route path="payment/error" element={<PaymentError />} />
                  <Route path="payment/cancel" element={<PaymentCancel />} />
                  <Route path="cart" element={
                    <PrivateRoute>
                      <Cart />
                    </PrivateRoute>
                  } />
                  <Route path="profile" element={
                    <PrivateRoute>
                      <Profile />
                    </PrivateRoute>
                  } />
                  <Route path="my-courses" element={
                    <PrivateRoute>
                      <MyCourses />
                    </PrivateRoute>
                  } />
                  <Route path="payments" element={
                    <PrivateRoute>
                      <Payments />
                    </PrivateRoute>
                  } />
                  <Route path="ask-instructor" element={
                    <PrivateRoute>
                      <UserAskInstructor />
                    </PrivateRoute>
                  } />
                  <Route path="checkout" element={
                    <PrivateRoute>
                      <Checkout />
                    </PrivateRoute>
                  } />
                  <Route path="payment/success" element={
                    <PrivateRoute>
                      <PaymentSuccess />
                    </PrivateRoute>
                  } />
                  <Route path="payment/error" element={
                    <PrivateRoute>
                      <PaymentError />
                    </PrivateRoute>
                  } />
                  <Route path="payment/cancel" element={
                    <PrivateRoute>
                      <PaymentCancel />
                    </PrivateRoute>
                  } />
                  <Route path="order-confirmation/:orderId" element={
                    <PrivateRoute>
                      <OrderConfirmation />
                    </PrivateRoute>
                  } />
                  <Route path="installment-details/:orderId" element={
                    <PrivateRoute>
                      <InstallmentDetails />
                    </PrivateRoute>
                  } />
                  <Route path="chatbot" element={<ChatBot />} />
                </Route>
                <Route path="courses/:courseId/learn/:lessonId" element={<LearnLayout><CourseLearn /></LearnLayout>} />
                <Route path="/register" element={<Register />} />
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/admin" element={<SecureAdminLogin />} />
                {/* وصول مباشر لصفحة تسجيل الدخول دون المرور بالآلة الحاسبة */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<MainLayout />}>
                  {/* لوحة القيادة - متاحة لجميع المديرين */}
                  <Route index element={<DashboardContent />} />
                  
                  {/* إدارة المستخدمين */}
                  <Route path="users/admins" element={
                    <PrivateRoute requiredPermission="view_admins">
                      <AdminsPage />
                    </PrivateRoute>
                  } />
                  <Route path="users/instructor" element={
                    <PrivateRoute requiredPermission="view_instructors">
                      <InstructorPage />
                    </PrivateRoute>
                  } />
                  <Route path="users/trainees" element={
                    <PrivateRoute requiredPermission="view_trainees">
                      <TraineesPage />
                    </PrivateRoute>
                  } />
                  <Route path="users/devices" element={
                    <PrivateRoute requiredPermission="view_devices">
                      <UserDevices />
                    </PrivateRoute>
                  } />
                  
                  {/* إدارة الدورات */}
                  <Route path="courses" element={
                    <PrivateRoute requiredPermission="view_courses">
                      <CoursesListPage />
                    </PrivateRoute>
                  } />
                  <Route path="courses/create" element={
                    <PrivateRoute requiredPermission="create_course">
                      <CreateCoursePage />
                    </PrivateRoute>
                  } />
                  <Route path="courses/edit/:id" element={
                    <PrivateRoute requiredPermission="edit_course">
                      <EditCoursePage />
                    </PrivateRoute>
                  } />
                  <Route path="courses/content" element={
                    <PrivateRoute requiredPermission="view_course_content">
                      <ContentPage />
                    </PrivateRoute>
                  } />
                  <Route path="courses/content/:courseId/manage" element={
                    <PrivateRoute requiredPermission="create_course_content">
                      <CourseContentManager />
                    </PrivateRoute>
                  } />
                  <Route path="courses/exams" element={
                    <PrivateRoute requiredPermission="view_exams">
                      <ExamsPage />
                    </PrivateRoute>
                  } />
                  <Route path="courses/exams/create" element={
                    <PrivateRoute requiredPermission="create_exam">
                      <CreateExamPage />
                    </PrivateRoute>
                  } />
                  <Route path="courses/exams/preview/:examId" element={
                    <PrivateRoute requiredPermission="view_exams">
                      <ExamPreviewPage />
                    </PrivateRoute>
                  } />
                  <Route path="courses/exams/edit/:id" element={
                    <PrivateRoute requiredPermission="edit_exam">
                      <EditExamPage />
                    </PrivateRoute>
                  } />
                  <Route path="courses/groups" element={
                    <PrivateRoute requiredPermission="view_courses">
                      <GroupsPage />
                    </PrivateRoute>
                  } />
                  <Route path="courses/categories" element={
                    <PrivateRoute requiredPermission="view_categories">
                      <CategoriesPage />
                    </PrivateRoute>
                  } />
                  <Route path="courses/enrollments" element={
                    <PrivateRoute requiredPermission="view_courses">
                      <EnrollmentsPage />
                    </PrivateRoute>
                  } />
                  <Route path="devices/users" element={
                    <PrivateRoute requiredPermission="view_devices">
                      <UserInfoPage />
                    </PrivateRoute>
                  } />
                  <Route path="devices/user-devices" element={
                    <PrivateRoute requiredPermission="view_devices">
                      <UserDevices />
                    </PrivateRoute>
                  } />
                  
                  {/* الخصومات وكوبونات */}
                  <Route path="discounts/courses" element={
                    <PrivateRoute requiredPermission="view_course_discounts">
                      <CourseDiscounts />
                    </PrivateRoute>
                  } />
                  <Route path="discounts/coupons" element={
                    <PrivateRoute requiredPermission="view_coupons">
                      <CouponDiscounts />
                    </PrivateRoute>
                  } />
                  
                  {/* المدفوعات */}
                  <Route path="payments/bank-transfers" element={
                    <PrivateRoute requiredPermission="view_bank_transfers">
                      <BankTransfers />
                    </PrivateRoute>
                  } />
                  
                  {/* الكتب */}
                  <Route path="books" element={
                    <PrivateRoute requiredPermission="view_books">
                      <BooksList />
                    </PrivateRoute>
                  } />
                  <Route path="books/create" element={
                    <PrivateRoute requiredPermission="create_book">
                      <CreateBook />
                    </PrivateRoute>
                  } />
                  <Route path="books/edit/:id" element={
                    <PrivateRoute requiredPermission="edit_book">
                      <EditBook />
                    </PrivateRoute>
                  } />
                  <Route path="books/orders" element={
                    <PrivateRoute requiredPermission="view_books">
                      <BookOrders />
                    </PrivateRoute>
                  } />
                  
                  {/* إدارة التقييمات والمراجعات */}
                  <Route path="reviews" element={
                    <PrivateRoute requiredPermission="view_reviews">
                      <CustomerReviews />
                    </PrivateRoute>
                  } />
                  
                  {/* إدارة تجارب المشتركين */}
                  <Route path="experiences" element={
                    <PrivateRoute requiredPermission="view_reviews">
                      <ExperiencesPage />
                    </PrivateRoute>
                  } />
                  
                  {/* اسأل المدرب */}
                  <Route path="ask-instructor" element={
                    <PrivateRoute requiredPermission="view_instructor_questions">
                      <AskInstructor />
                    </PrivateRoute>
                  } />
                  <Route path="ask-instructor/:id" element={
                    <PrivateRoute requiredPermission="view_instructor_questions">
                      <ChatCard />
                    </PrivateRoute>
                  } />
                  <Route path="ask-instructor/chat/:id" element={
                    <PrivateRoute requiredPermission="view_instructor_questions">
                      <ChatCard />
                    </PrivateRoute>
                  } />
                  
                  {/* الإعدادات */}
                  <Route path="settings/*" element={
                    <PrivateRoute requiredPermission="view_settings">
                      <Settings />
                    </PrivateRoute>
                  } />
                  
                  {/* المحادثة */}
                  <Route path="chat" element={
                    <PrivateRoute requiredPermission="view_course_content">
                      <CourseChat />
                    </PrivateRoute>
                  } />
                  <Route path="payment/electronic" element={
                    <PrivateRoute requiredPermission="view_bank_transfers">
                      <ElectronicPayment />
                    </PrivateRoute>
                  } />
                </Route>
                {/* توجيه المسار /dashboard إلى صفحة الخطأ 404 */}
                <Route path="/dashboard" element={<Navigate to="/404" replace />} />
                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              <ToastContainer
                position="bottom-left"
                rtl
              />
            </SidebarProvider>
          </MaintenanceMiddleware>
        </PermissionsProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
}

export default App;
